import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { formatDate } from '@simsm/ui-shared';
import { assessCorrectionLots, type LotRefusal } from './api';

interface LotIdentity {
  medicineCode: string;
  lot: string;
}

/**
 * What a report row's "Corectează expirare" is (docs/shape-ops-expiry-correction-gate.md
 * D4): offered when the report's window is open and core would accept a request
 * for the lot. Otherwise shut, naming the first reason that holds: the lot's
 * refusal, the report's closed window, a verdict still on its way, a verdict
 * that never came.
 */
export type ExpiryActionState =
  | { kind: 'open' }
  | { kind: 'refused'; refusal: LotRefusal }
  | { kind: 'window_closed' }
  | { kind: 'checking' }
  | { kind: 'unknown' };

export function expiryActionState(input: {
  verdictStatus: 'pending' | 'error' | 'success';
  refusal: LotRefusal | '' | undefined;
  reportWindowOpen: boolean;
}): ExpiryActionState {
  if (input.refusal) return { kind: 'refused', refusal: input.refusal };
  if (!input.reportWindowOpen) return { kind: 'window_closed' };
  if (input.verdictStatus === 'pending') return { kind: 'checking' };
  if (input.verdictStatus === 'error' || input.refusal === undefined) return { kind: 'unknown' };
  return { kind: 'open' };
}

const REFUSAL_KEYS: Record<LotRefusal, string> = {
  LOT_NOT_REPORTED: 'submission_detail.expiry_correction.not_reported',
  LOT_NO_POSITIVE_STOCK: 'submission_detail.expiry_correction.no_positive_stock',
  CORRECTION_WINDOW_EXPIRED: 'submission_detail.expiry_correction.window_expired',
  ALREADY_PENDING: 'submission_detail.expiry_correction.already_pending',
};

function lotKey(row: LotIdentity): string {
  return `${row.medicineCode}\u0000${row.lot}`;
}

/**
 * The report's lots assessed once per report, and the state and title of each
 * row's action. Runs only for a role that may request a correction.
 */
export function useExpiryCorrectionGate(input: {
  submissionId: string;
  rows: readonly LotIdentity[];
  enabled: boolean;
  reportWindow: { open: boolean; days: number; deadlineIso: string };
}): (row: LotIdentity) => { disabled: boolean; title: string } {
  const { t } = useTranslate();
  const lots = useMemo(() => {
    const distinct = new Map<string, LotIdentity>();
    for (const row of input.rows) {
      distinct.set(lotKey(row), { medicineCode: row.medicineCode, lot: row.lot });
    }
    return Array.from(distinct.values());
  }, [input.rows]);

  const verdicts = useQuery({
    queryKey: ['correction-eligibility', input.submissionId],
    queryFn: () => assessCorrectionLots(lots),
    enabled: input.enabled && lots.length > 0,
  });

  const refusals = useMemo(
    () => new Map((verdicts.data?.lots ?? []).map((lot) => [lotKey(lot), lot.refusal])),
    [verdicts.data],
  );
  const deadline = formatDate(input.reportWindow.deadlineIso);

  return (row) => {
    const state = expiryActionState({
      verdictStatus: verdicts.status,
      refusal: refusals.get(lotKey(row)),
      reportWindowOpen: input.reportWindow.open,
    });
    switch (state.kind) {
      case 'open':
        return {
          disabled: false,
          title: t('submission_detail.correction_window.open_hint', { deadline }),
        };
      case 'refused':
        return {
          disabled: true,
          title: t(REFUSAL_KEYS[state.refusal], {
            days: verdicts.data?.correctionWindowDays ?? input.reportWindow.days,
          }),
        };
      case 'window_closed':
        return {
          disabled: true,
          title: t('submission_detail.correction_window.closed_hint', {
            days: input.reportWindow.days,
            deadline,
          }),
        };
      case 'checking':
        return { disabled: true, title: t('submission_detail.expiry_correction.checking') };
      case 'unknown':
        return { disabled: true, title: t('submission_detail.expiry_correction.unknown') };
    }
  };
}
